import {
    Edit,
    SimpleForm,
    TextField,
    SelectField,
    SelectInput,
    TextInput,
    required,
    maxLength,
    minLength,
    usePermissions,
    FunctionField,
    useRecordContext,
    TopToolbar,
    useTranslate,
    ReferenceInput,
    AutocompleteInput,
    ArrayField,
    SingleFieldList,
    regex,
    BooleanInput,
    CheckboxGroupInput,
} from 'react-admin';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomCreate from '@/components/global/list/CustomCreate.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import CustomShowModalButton from '@/components/global/buttons/CustomShowModalButton.jsx';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomCreateButton from '@/components/global/buttons/CustomCreateButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { useDrawer } from '@/composables/useDrawer';
import { RightDrawer } from '@/components/global/drawers/RightDrawer';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import { useTheme, useMediaQuery, Box, Chip } from '@mui/material';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { DrawerLink } from '@/components/global/drawers/DrawerLink.jsx';
import { deploymentStatusShortChoices, softwareTypeChoices } from '@/components/global/options/selectOptions.jsx';
import { ReferenceArrayFilter, Select1or0Filter } from '@/components/global/filters/filters.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { aliasFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar.jsx';
import { useFormContext, useWatch } from 'react-hook-form';
import { aliasToString } from '@/helpers/device/softwareHelpers.js';
import { CustomBooleanField } from '@/components/global/fields/CustomBooleanField.jsx';
import CustomDeleteButton from '@/components/global/buttons/CustomDeleteButton.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';

const getFilters = (isMobile, translate) => [
    <SearchBar key='search' source='value' labelKey='common.search' alwaysOn />,
    <TextInput key='serialNumber' source='serialNumber' label={translate('resources.softwareUpdateRule.filters.serialNumber')} alwaysOn={isMobile} />,
    <CustomSelectArrayInput
        key='allowedStatuses'
        source='allowedStatuses_any'
        label={translate('resources.softwareUpdateRule.filters.allowedStatuses')}
        choices={deploymentStatusShortChoices(translate)}
        alwaysOn={isMobile}
        forceDesktopOnMobile={true}
    />,
    <CustomSelectArrayInput
        key='type'
        source='alias->software->type_in'
        label={translate('resources.softwareUpdateRule.filters.type')}
        choices={softwareTypeChoices}
        alwaysOn={isMobile}
        forceDesktopOnMobile={true}
    />,
    <ReferenceArrayFilter
        key='alias'
        source='aliasId_in'
        reference='software-alias'
        optionText={choice => `${choice.software.type} - ${aliasToString(choice)}`}
        label={translate('resources.softwareUpdateRule.filters.alias')}
        forceDesktopOnMobile={true}
        alwaysOn={isMobile}
    />,
    <Select1or0Filter key='isActive' source='isActive' label={translate('resources.softwareUpdateRule.filters.active')} alwaysOn={isMobile} />,
];

const MoreActionsPopover = ({ permissions }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { openDrawer } = useDrawer();
    const canShow = canViewDetails('software-update-rule', permissions);

    if (!record || (!canShow && !permissions.isAdmin)) return null;

    return (
        <CustomMorePopover permissions={permissions} record={record}>
            {canShow && (
                <CustomShowModalButton
                    label={translate('resources.softwareUpdateRule.labels.show')}
                    onOpen={() => openDrawer(record.id, 'software-update-rule-show')}
                />
            )}
            {permissions.isAdmin && (
                <>
                    <CustomEditModalButton
                        label={translate('resources.softwareUpdateRule.labels.edit')}
                        onOpen={() => openDrawer(record.id, 'software-update-rule-edit')}
                    />
                    <CustomDeleteButton resource='software-update-rule' id={record.id} label={translate('resources.softwareUpdateRule.labels.delete')} />
                </>
            )}
        </CustomMorePopover>
    );
};

MoreActionsPopover.propTypes = {
    permissions: PropTypes.shape({
        isAdmin: PropTypes.bool,
        isBok: PropTypes.bool,
    }),
};

const ListActions = () => {
    const { isLoading, permissions } = usePermissions();
    const translate = useTranslate();
    if (isLoading) return null;

    return (
        <TopToolbar>
            <SearchFilterInput />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} />
            {permissions.isAdmin && (
                <CustomToolbarActionsPopover>
                    <CustomCreateButton label={translate('resources.softwareUpdateRule.labels.create')} />
                </CustomToolbarActionsPopover>
            )}
        </TopToolbar>
    );
};

export const SoftwareUpdateRuleList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { openDrawer } = useDrawer();

    const translate = useTranslate();
    const filters = useMemo(() => getFilters(isMobile, translate), [isMobile, translate]);

    if (isLoading) return null;

    return (
        <CustomList filters={filters} actions={<ListActions />} addLastColumnShadow={true} empty={false}>
            <CustomDatagridConfigurable
                bulkActionButtons={false}
                rowClick={id => {
                    openDrawer(id, 'software-update-rule-show');
                    return false;
                }}
            >
                <TextField source='name' label={translate('resources.softwareUpdateRule.fields.name')} />
                <TextField source='pattern' label={translate('resources.softwareUpdateRule.fields.pattern')} />
                <ArrayField source='allowedStatuses' label={translate('resources.softwareUpdateRule.fields.allowedStatuses')} sortable={false}>
                    <SingleFieldList linkType={false}>
                        <StatusChip />
                    </SingleFieldList>
                </ArrayField>
                <SelectField source='alias.software.type' choices={softwareTypeChoices} label={translate('resources.softwareUpdateRule.fields.type')} />
                <FunctionField
                    source='aliasId'
                    sortBy='alias.software.version'
                    label={translate('resources.softwareUpdateRule.fields.alias')}
                    render={record => (
                        <Box onClick={event => event.stopPropagation()}>
                            <DrawerLink resource='software' action='show' source={record.alias.softwareId} label={aliasToString(record.alias)} relative />
                        </Box>
                    )}
                />
                <CustomBooleanField source='isActive' label={translate('resources.softwareUpdateRule.fields.isActive')} />
                <FunctionField
                    label={translate('common.options')}
                    render={() => (
                        <Box onClick={event => event.stopPropagation()}>
                            <MoreActionsPopover permissions={permissions} />
                        </Box>
                    )}
                />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

export const SoftwareUpdateRuleCreate = ({ handleClose }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.softwareUpdateRule.labels.create')}>
            <CustomCreate handleClose={handleClose}>
                <Form handleClose={handleClose} />
            </CustomCreate>
        </RightDrawer>
    );
};

export const SoftwareUpdateRuleEdit = ({ handleClose, elementId }) => {
    const translate = useTranslate();

    return (
        <RightDrawer handleClose={handleClose} title={translate('resources.softwareUpdateRule.labels.edit')}>
            <Edit
                mutationMode='pessimistic'
                id={elementId}
                actions={false}
                redirect={resource => {
                    handleClose();
                    return resource;
                }}
            >
                <Form handleClose={handleClose} isEdit />
            </Edit>
        </RightDrawer>
    );
};

const Form = ({ handleClose, isEdit = false }) => {
    const translate = useTranslate();

    return (
        <SimpleForm toolbar={<TagCategoryToolbar onCancel={handleClose} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />}>
            <Box sx={{ width: '100%' }}>
                <TextInput
                    source='name'
                    label={translate('resources.softwareUpdateRule.fields.name')}
                    validate={[
                        required(translate('common.validation.required')),
                        minLength(3, translate('common.validation.minLength', { count: 3 })),
                        maxLength(100, translate('common.validation.maxLength', { count: 100 })),
                    ]}
                    fullWidth
                />
                <TextInput
                    source='pattern'
                    label={translate('resources.softwareUpdateRule.fields.pattern')}
                    validate={[
                        required(translate('common.validation.required')),
                        regex(/^SN[\d*]{2}-[\d*]{2}-[\d*]{3}-[\d*]{3}-[\d*]{4}$/, translate('resources.softwareUpdateRule.validation.invalidPattern')),
                    ]}
                    fullWidth
                />
                <Box sx={{ marginLeft: '10px' }}>
                    <CheckboxGroupInput
                        source='allowedStatuses'
                        label={translate('resources.softwareUpdateRule.fields.allowedStatuses')}
                        choices={deploymentStatusShortChoices(translate)}
                        validate={[required(translate('common.validation.required'))]}
                        defaultValue={['new', 'productionDone', 'readyForDeployment']}
                        row={false}
                        fullWidth
                    />
                </Box>
                <TypeInput disabled={isEdit} />
                <AliasInput />
                <Box sx={{ marginLeft: '10px' }}>
                    <BooleanInput source='isActive' label={translate('resources.softwareUpdateRule.fields.isActive')} defaultValue={true} size='small' />
                </Box>
            </Box>
        </SimpleForm>
    );
};

Form.propTypes = {
    handleClose: PropTypes.func.isRequired,
    isEdit: PropTypes.bool,
};

const TypeInput = ({ disabled = false }) => {
    const translate = useTranslate();
    const { setValue } = useFormContext();

    const handleChange = () => {
        setValue('aliasId', null);
    };

    return (
        <SelectInput
            source='alias.software.type'
            choices={softwareTypeChoices}
            onChange={handleChange}
            label={translate('resources.softwareUpdateRule.fields.type')}
            validate={[required(translate('common.validation.required'))]}
            disabled={disabled}
            fullWidth
        />
    );
};

TypeInput.propTypes = {
    disabled: PropTypes.bool,
};

const AliasInput = () => {
    const translate = useTranslate();
    const type = useWatch({ name: 'alias.software.type' });

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='aliasId' reference='software-alias' filter={{ 'software.type': type }} enableGetChoices={() => !!type}>
                <AutocompleteInput
                    filterToQuery={aliasFilterToQuery}
                    validate={required(translate('common.validation.required'))}
                    readOnly={!type}
                    fullWidth={true}
                    disablePortal={true}
                    label={translate('resources.softwareUpdateRule.fields.alias')}
                />
            </ReferenceInput>
        </Box>
    );
};

const StatusChip = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const statusName = deploymentStatusShortChoices(translate).find(choice => choice.id === record)?.name || record;

    return <Chip label={statusName} />;
};
